import { Grid, Button, Typography, styled } from "@mui/material";
import { useMemo } from "react";
import WarningIcon from "@mui/icons-material/Warning";
import { debounce } from "lodash";

export interface ILastModifiedInfo {
  modifiedBy: string;
  modifiedAt: Date;
}

interface IEditorChangeWarningProps {
  eventId: string;
  time: Date | number;
  outsideModification?: ILastModifiedInfo;
  refreshForm: (id: string) => Promise<void>;
  eventType: "Flight" | "Ground Event";
}

const OCEditorChangeWarning = styled(Grid)(({ theme }) => ({
  "@keyframes flash": {
    "0%": { color: theme.palette.secondary.main },
    "25%": { color: theme.palette.primary.contrastText },
    "50%": { color: theme.palette.secondary.main },
    "75%": { color: theme.palette.primary.contrastText },
    "100%": { color: theme.palette.secondary.main },
  },
  "@keyframes delayDisplay": {
    "0%": { maxHeight: 0, margin: 0 },
    "99%": { maxHeight: 0, margin: 0 },
    "100%": { maxHeight: "unset", margin: 12 },
  },
  animation: "1s linear normal forwards linear", // display after 1 second.
  animationName: "$delayDisplay", // This animations adds a 1 second delay to display of the warning (enough time for internal changes to upset the form without user seeing the warning.)
  overflow: "hidden",
  "& .warning_flash": {
    color: theme.palette.secondary.main,
    animation: "3s linear normal forwards ease-in-out", // flash for 3 seconds to get user attention.
    animationName: "$flash",
  },
}));

/**
 * A component used to warn the user if a flight has been modified outside of their open editor and allow them to refresh it.
 * Will display above any open tab in the event editor (flights and ground events).
 * @param props- userId - the id of the user who made the changes & the time
 */
export const EditorChangeWarning = (props: IEditorChangeWarningProps): JSX.Element => {
  const { eventId, time, outsideModification, refreshForm, eventType } = props;

  // Return true if there's outside modification info and it's date exceeds the time passed in.
  const eventModifiedOutsideEditor = useMemo(() => {
    return outsideModification?.modifiedAt && time ? new Date(outsideModification?.modifiedAt) > new Date(time) : false;
  }, [outsideModification?.modifiedAt, time]);

  // Only display if flight has been modified outside of the editor.
  return eventModifiedOutsideEditor ? (
    <OCEditorChangeWarning container>
      <WarningIcon className="warning_flash" style={{ width: 14, height: 14, marginRight: 5 }} />
      <Typography className="warning_flash" style={{ fontSize: 11 }}>
        {outsideModification?.modifiedBy
          ? `${eventType} has been modified outside of editor.`
          : `${eventType} has been removed or cancelled outside of editor.`}
      </Typography>
      {outsideModification?.modifiedBy && (
        <Button
          style={{ fontSize: 10, padding: 0, height: 15, margin: "0px 5px" }}
          className="refresh_form_btn"
          onClick={debounce(() => {
            refreshForm(eventId);
          }, 1000)}
        >
          Click me to reload
        </Button>
      )}
    </OCEditorChangeWarning>
  ) : (
    <></>
  );
};
